import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SafeArea } from "@/components/ios/SafeArea";
import { ScreenHeader } from "@/components/ios/ScreenHeader";
import { UploadDropzone } from "./UploadDropzone";
import { FileRow } from "./FileRow";
import { useKnowledgeList, useUploadKnowledge, useDeleteKnowledge } from "./useKnowledge";
import { cn } from "@/lib/utils";
import { useColonyContext } from "@/hooks/useColonyContext";
import { KnowledgeColonySelector } from "@/components/KnowledgeColonySelector";
import { KnowledgeEmptyState } from "@/components/KnowledgeEmptyState";
import { sessionPersistence } from "@/utils/sessionPersistence";

export function KnowledgeScreen() {
  const { colonyId } = useParams<{ colonyId: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { activeColonyId, activeColony, hasColonies, setActiveColony } = useColonyContext();

  // Use colonyId from URL or fall back to active colony context
  const effectiveColonyId = colonyId || activeColonyId;
  const effectiveColony = activeColony;

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Use effectiveColonyId with fallback to prevent hook errors
  const { data: files = [], isLoading } = useKnowledgeList(effectiveColonyId || "");
  const uploadMutation = useUploadKnowledge(effectiveColonyId || "");
  const deleteMutation = useDeleteKnowledge(effectiveColonyId || "");

  // Track knowledge access for session persistence
  useEffect(() => {
    if (effectiveColonyId) {
      setActiveColony(effectiveColonyId);
      sessionPersistence.setLastActive(effectiveColonyId, `/knowledge/${effectiveColonyId}`);
    }
  }, [effectiveColonyId, setActiveColony]);

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBack = () => {
    navigate("/");
  };

  const handleFileUpload = (file: File) => {
    if (effectiveColonyId) {
      uploadMutation.mutate(file);
    }
  };

  const handleFileDelete = (docId: string) => {
    if (effectiveColonyId) {
      deleteMutation.mutate(docId);
    }
  };

  // Handle no colonies case
  if (!hasColonies) {
    return (
      <SafeArea>
        <ScreenHeader title="Knowledge" onBack={() => navigate("/")} />
        <KnowledgeEmptyState type="no-colonies" />
      </SafeArea>
    );
  }

  // Handle no specific colony selected
  if (!effectiveColonyId || !effectiveColony) {
    return (
      <SafeArea>
        <ScreenHeader title="Knowledge" onBack={() => navigate("/")} />
        <KnowledgeEmptyState type="select-colony" />
      </SafeArea>
    );
  }

  return (
    <SafeArea>
      <ScreenHeader title="Knowledge" right={<KnowledgeColonySelector />} onBack={handleBack} />

      {/* Search */}
      <div className="px-4 py-3 border-b border-ios-separator bg-background">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search files..."
            className={cn(
              "pl-10 h-10 bg-ios-surface-secondary border-transparent rounded-lg",
              "focus:ring-2 focus:ring-primary/20"
            )}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scroll-pb-tabbar">
        {/* Upload Section */}
        <div className="p-4">
          <UploadDropzone
            onFileUpload={handleFileUpload}
            isUploading={uploadMutation.isPending}
          />
        </div>

        {/* Files List */}
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-muted-foreground ios-body">Loading files...</div>
            </div>
          ) : filteredFiles.length === 0 ? (
            searchTerm ? (
              <div className="text-center py-8">
                <div className="text-muted-foreground ios-body mb-2">
                  No files match your search
                </div>
                <div className="text-muted-foreground ios-caption-1">
                  Try a different search term
                </div>
              </div>
            ) : (
              <KnowledgeEmptyState type="no-files" colonyName={effectiveColony.name} />
            )
          ) : (
            <div className="space-y-2">
              <div className="ios-caption-1 text-muted-foreground px-2 pb-2">
                {filteredFiles.length} {filteredFiles.length === 1 ? "file" : "files"}
              </div>
              {filteredFiles.map((file, idx) => (
                <FileRow
                  key={(file as any)?.docId || (file as any)?.id || `${(file as any)?.name || 'file'}-${idx}`}
                  file={file}
                  onDelete={() => handleFileDelete(file.docId)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </SafeArea>
  );
}